<template>
    <modal-dialog
        ref="forwardLeadModal"
        label="Lead doorsturen naar ander team"
        description="Met dit formulier kan je je lead doorsturen naar een ander team"
        :size="4"
        without-padding
    >
        <template #default>
            <form-validation v-slot="{ bindings: validationBindings }">
                <flex-container :padding="Size.lg" :gap="Size.sm">
                    <flex-container :gap="Size.sm">
                        <text-heading sub>
                            Lead doorsturen naar team
                        </text-heading>
                    </flex-container>
                    <flex-container>
                        <flex-container :size="5">
                            <input-group v-model="form.team">
                                <template #label>
                                    <input-label>
                                        Kies een team
                                    </input-label>
                                </template>
                                <template #input>
                                    <team-select-input :teams="teams" />
                                </template>
                            </input-group>
                        </flex-container>
                        <flex-container :size="5">
                            <input-group v-model="form.user">
                                <template #label>
                                    <input-label>
                                        Kies een gebruiker (optioneel)
                                    </input-label>
                                </template>
                                <template #input>
                                    <user-select-input :users="teamUsers" />
                                </template>
                            </input-group>
                        </flex-container>
                    </flex-container>
                    <input-group v-model="form.inform">
                        <template #input>
                            <switch-input variant="small" />
                            <span>&nbsp;Informeer {{ teamName }} over deze toewijzing</span>
                        </template>
                    </input-group>
                    <flex-container :gap="Size.xs" justify-content="flex-end">
                        <input-group v-model="form.comment">
                            <template #label>
                                <input-label>
                                    Notitie (optioneel)
                                </input-label>
                            </template>
                            <template #input>
                                <rich-text-area-input :mentionables="mentionables" />
                            </template>
                        </input-group>
                        <input-group v-model="form.commentVisibility" inline>
                            <template #input>
                                <comment-visibility-input />
                            </template>
                        </input-group>
                    </flex-container>
                </flex-container>
                <flex-container
                    align-items="center"
                    justify-content="flex-end"
                    :color="Color.gray100"
                    :padding="Size.md"
                >
                    <button-outline @click="redirectToDetail">
                        Annuleren
                    </button-outline>
                    <button-block-confirm
                        :disabled="!validationBindings.isValid.value"
                        @confirmed="save($event)"
                    >
                        Doorsturen
                        <template #confirmation>
                            Bevestigen
                        </template>
                    </button-block-confirm>
                </flex-container>
            </form-validation>
        </template>
    </modal-dialog>
</template>

<script lang="ts" setup>
    import {
        Color,
        type ExposedOverlay,
        ModalDialog,
        ButtonOutline,
        ButtonBlockConfirm,
        Size,
        SwitchInput,
        FlexContainer,
        FormValidation,
        InputGroup,
        InputLabel,
        RichTextAreaInput,
        TextHeading,
        type ButtonStateChangedEvent,
        ButtonState,
    } from '@keyvue/one-ui';
    import { computed, onMounted, ref, type Ref, watch } from 'vue';
    import { useRouter } from 'vue-router';
    import { Lead, User, Team, CommentVisibility, type TeamSubResource, type PipelineSubResource, Pipeline } from '@keyvue/one-api-client';
    import useOverlayAsRoute from '@composables/useOverlayAsRoute';
    import CommentVisibilityInput from '@components/Inputs/CommentVisibilityInput.vue';
    import UserSelectInput from '@components/Inputs/UserSelectInput.vue';
    import TeamSelectInput from '@components/Inputs/TeamSelectInput.vue';
    import useClient from '@composables/useClient';
    import useMentionable from '@composables/useMentionable';
    import { Comment as LegacyComment, CommentOrigin as LegacyCommentOrigin, CommentVisibility as LegacyCommentVisibility, CommentAPI, createResourceIdentifier, ReferralAPI } from '@keyvue/one-api-client-legacy';
    import { v4 } from 'uuid';
    import useParentRoute from '@composables/useParentRoute';
    import useEventBus from '@composables/useEventBus';

    const { client } = useClient();
    const router = useRouter();
    const eventBus = useEventBus();
    const lead = ref<Lead>(await client.get(Lead, router.currentRoute.value.params.leadId.toString()));
    const createRouteLocation = useParentRoute();
    const forwardLeadModal = ref<ExposedOverlay>();

    const teams = ref<Team[]>([]) as Ref<Team[]>;
    const teamUsers = ref<User[]>([]) as Ref<User[]>;
    const mentionable = useMentionable(teamUsers);
    const mentionables = computed(() => mentionable.mentionables.value);

    const form = ref<{
        team: Team | null,
        user: User | null,
        inform: boolean,
        comment: string,
        commentVisibility: { value: CommentVisibility, label: string },
    }>({
        team: null,
        user: null,
        inform: true,
        comment: '',
        commentVisibility: CommentVisibility.options[0],
    });

    const redirectToDetail = () => {
        router.push(createRouteLocation({
            name: 'LeadDetail',
            params: {
                leadId: lead.value?.id.value,
            },
        }, 'contact'));
    };

    useOverlayAsRoute({
        overlay: forwardLeadModal,
        isSideSheet: false,
        redirectMethod: redirectToDetail,
    });

    onMounted(async () => {
        if (!lead.value) {
            return;
        }

        const teamCollection = await client.getSubResources<PipelineSubResource, Team>(
            lead.value.pipeline as Pipeline,
            Team,
            'available-teams',
        );
        teams.value = teamCollection.data;
    });

    const teamName = computed(() => (form.value.team ? form.value.team.name : 'het team'));

    const save = async (event: ButtonStateChangedEvent) => {
        if (!lead.value || !client.session.userId) {
            return;
        }

        const legacyReferral = await ReferralAPI.get(lead.value.id.value);

        const newLegacyComment = new LegacyComment(
            createResourceIdentifier('referrals', lead.value.id.value),
            createResourceIdentifier('users', client.session.userId.value),
            v4(),
            form.value.comment,
            LegacyCommentOrigin.USER,
            form.value.commentVisibility.value.value as LegacyCommentVisibility,
            new Date(),
            new Date(),
        );

        if (form.value.team) {
            legacyReferral.assignedToBranch = createResourceIdentifier(
                'branches',
                form.value.team.id.value,
            );
        }

        if (form.value.user) {
            legacyReferral.assignedTo = createResourceIdentifier(
                'users',
                form.value.user.id.value,
            );
        }

        await ReferralAPI.update(lead.value.id.value, legacyReferral);
        await CommentAPI.create(newLegacyComment);
        await mentionable.mention(newLegacyComment);
        event.button.setState(ButtonState.completed);
        eventBus.dispatch('resource-manipulated', 'Lead');
        redirectToDetail();
    };

    watch(form, async () => {
        if (!form.value.team) {
            return;
        }

        const userCollection = await client.getSubResources<TeamSubResource, User>(
            form.value.team as Team,
            User,
            'available-users',
        );
        teamUsers.value = userCollection.data;
    }, { deep: true });
</script>
